/**
 * The personal record — the ledger of readings, kept on this device.
 *
 * The server never receives this. It holds anonymous, consented contributions;
 * the record of who they belonged to and what they said stays here. That split
 * is the architecture, not an optimisation, and it is why the consent screen
 * means something.
 */

import api from './api.js';
import { destroyIdentity } from './identity.js';
import { clear as clearOutbox } from './outbox.js';

const KEY = 'omnialera.ori.record.v1';
const MAX_READINGS = 500;

export const SCOPES = Object.freeze(['corpus', 'routing', 'cohort', 'assistant']);

const EMPTY = {
  monkTone: null,
  readings: [],
  consent: { corpus: false, routing: false, cohort: false, assistant: false },
  credits: 0,
  createdAt: null,
};

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...EMPTY, consent: { ...EMPTY.consent } };
    const parsed = JSON.parse(raw);
    return {
      ...EMPTY,
      ...parsed,
      consent: { ...EMPTY.consent, ...(parsed.consent ?? {}) },
      readings: Array.isArray(parsed.readings) ? parsed.readings : [],
    };
  } catch {
    // A corrupt or unreadable store must not brick the app.
    return { ...EMPTY, consent: { ...EMPTY.consent } };
  }
}

function write(next) {
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Private mode, or the quota is full. The session still works; nothing persists.
  }
  return next;
}

const listeners = new Set();
let state = read();

function emit() {
  for (const fn of listeners) fn(state);
}

export const record = {
  get() {
    return state;
  },

  subscribe(fn) {
    listeners.add(fn);
    fn(state);
    return () => listeners.delete(fn);
  },

  setTone(monkTone) {
    state = write({ ...state, monkTone, createdAt: state.createdAt ?? new Date().toISOString() });
    emit();
  },

  /** Add a reading locally. Donation to the corpus is a separate, consented step. */
  addReading(reading) {
    const readings = [...state.readings, reading].slice(-MAX_READINGS);
    state = write({ ...state, readings, createdAt: state.createdAt ?? new Date().toISOString() });
    emit();
    return reading;
  },

  /**
   * Mark exactly the readings that reached the corpus.
   *
   * This used to stamp whichever reading was last in the list, on the
   * assumption that a successful send meant the newest one had gone. It does
   * not: the queue drains oldest-first, so an older entry succeeding while the
   * newest failed marked the newest as shared — and a backlog flushing after a
   * later local-only reading marked *that* reading as shared, when it had never
   * been sent anywhere at all. In an app whose whole claim is that it does not
   * assert what it cannot stand behind, the record was doing exactly that.
   *
   * `observedAt` is the correlation key: it is on the local reading and on the
   * queued copy, and two readings cannot share one — a scan takes tens of
   * seconds and the field has millisecond resolution.
   *
   * @param {string[]} keys observedAt values that were actually delivered
   */
  markSharedByKeys(keys) {
    if (!Array.isArray(keys) || keys.length === 0) return;
    const wanted = new Set(keys);
    let changed = false;
    const readings = state.readings.map((r) => {
      if (!wanted.has(r.observedAt) || r.shared) return r;
      changed = true;
      return { ...r, shared: true };
    });
    if (!changed) return;
    state = write({ ...state, readings });
    emit();
  },

  applyCredits(balance) {
    if (typeof balance !== 'number') return;
    state = write({ ...state, credits: balance });
    emit();
  },

  /** Mirror the server's view of consent, which is the authoritative one. */
  syncConsent(scopes) {
    const consent = { ...state.consent };
    for (const scope of SCOPES) {
      if (scopes?.[scope]) consent[scope] = Boolean(scopes[scope].granted);
    }
    state = write({ ...state, consent });
    emit();
  },

  setConsentLocal(scope, granted) {
    state = write({ ...state, consent: { ...state.consent, [scope]: granted } });
    emit();
  },

  /** Forget the readings but keep the identity and consent state. */
  clearReadings() {
    state = write({ ...state, readings: [] });
    emit();
  },

  /**
   * Full erasure.
   *
   * Order matters and is the whole point: revoke every scope FIRST, while the
   * device can still sign, then destroy the key. Destroying the key first would
   * leave contributions in the corpus that could never be withdrawn, which
   * would make the promise on the consent screen false.
   */
  async eraseEverything() {
    const failures = [];
    // With no corpus service configured there is nothing on a server to
    // withdraw from — nothing was ever sent. Attempting it anyway POSTed to the
    // app's own origin, got a 404 from the static host, and made erasure
    // permanently impossible: the person was told to try again when they were
    // back online, about a network that had never been involved.
    if (api.configured) {
      for (const scope of SCOPES) {
        if (!state.consent[scope]) continue;
        try {
          await api.setConsent(scope, false);
        } catch (err) {
          failures.push({ scope, message: err.message });
        }
      }
    }
    if (failures.length) {
      const scopes = failures.map((f) => f.scope).join(', ');
      throw new Error(
        `Could not withdraw consent for ${scopes}, so the device key has been kept. ` +
          'Try again when you are back online — erasing the key now would leave data that can never be withdrawn.',
      );
    }
    // The outbox holds readings that have not been sent yet. Erasing everything
    // has to mean those too, or "delete my data" would quietly leave a queue
    // that sends itself to the corpus the next time the phone finds a signal.
    await clearOutbox();
    await destroyIdentity();
    try {
      localStorage.removeItem(KEY);
    } catch {
      /* nothing to do */
    }
    state = { ...EMPTY, consent: { ...EMPTY.consent } };
    emit();
  },

  /** The whole record, as an open file. */
  exportBlob() {
    return JSON.stringify(
      {
        format: 'omnialera.ori.record.v1',
        exportedAt: new Date().toISOString(),
        note: 'Prototype export. Not a medical record.',
        monkTone: state.monkTone,
        consent: state.consent,
        credits: state.credits,
        readings: state.readings,
      },
      null,
      2,
    );
  },
};

/** Derived helpers used across views. */
export function summarise(readings) {
  const total = readings.length;
  const locked = readings.filter((r) => r.locked).length;
  const last = readings[readings.length - 1] ?? null;
  const recentFailures = readings.slice(-8).filter((r) => !r.locked).length;
  return { total, locked, last, recentFailures, successRate: total ? locked / total : null };
}
